import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
} from "@mui/material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { Controller, FieldValues } from "react-hook-form";
import { useForm } from "../../../../hooks/useForm";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { ActionButtons } from "../../../../components/form/ActionButtons";
import { SyncrosaleLoader } from "../../../../components/layout/components/SyncrosaleLoader";

type InventoryManagementFormValues = {
  productRemoval: string;
  productCreation: string;
};

export const InventoryManagementSettings = () => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const { translate } = useTranslation(
    "pages.stores.settings.inventoryManagementSettings",
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<InventoryManagementFormValues>({
    defaultValues: {
      productRemoval: "",
      productCreation: "",
    },
  });

  const { data, isLoading, isFetching } = useCustom({
    url: `store/${selectedCountry?.id}/inventory-settings`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
    },
  });
  useEffect(() => {
    const formValues = data?.data;
    if (!formValues) return;

    reset(formValues);
  }, [data]);

  const { mutate, isLoading: isSaveLoading } = useCustomMutation();

  const onSubmit = async (data: FieldValues) => {
    mutate(
      {
        url: `store/${selectedCountry?.id}/inventory-settings`,
        method: "put",
        values: data,
        successNotification: () => {
          return {
            message: translate("updatedSuccessfully"),
            type: "success",
          };
        },
      },
      {
        onSuccess: (data) => {
          reset(data?.data);
        },
      },
    );
  };

  if (isLoading || isFetching) {
    return <SyncrosaleLoader />;
  }

  return (
    <Container
      sx={{ padding: "16px", marginBottom: "16px", maxWidth: "600px" }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {[
          {
            name: "productRemoval" as keyof FieldValues,
            label: translate("productRemoval.label"),
            options: [
              {
                value: "NEVER_REMOVE_PRODUCTS",
                label: translate("neverRemoveProducts"),
              },
              {
                value: "REMOVE_PRODUCTS_NOT_PASSING_FILTERS",
                label: translate("removeProductsNotPassingFilters"),
              },
            ],
          },
          {
            name: "productCreation" as keyof FieldValues,
            label: translate("productCreation"),
            options: [
              {
                value: "DONT_ADD_ITEMS_WITHOUT_APPROVAL",
                label: translate("dontAddItemsWithoutApproval"),
              },
              {
                value: "REQUEST_APPROVAL_FOR_NEW_ITEMS",
                label: translate("requestApprovalForNewItems"),
              },
              {
                value: "AUTO_ADD_ITEMS_MEETING_FILTERS",
                label: translate("autoAddItemsMeetingFilters"),
              },
            ],
          },
        ].map(({ name, label, options }) => (
          <React.Fragment key={name}>
            <Box
              key={name}
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              mb={2}
              gap={2}
            >
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ width: { xs: "100%", sm: "20%" }, mb: { xs: 1, sm: 0 } }}
              >
                {label}:
              </Typography>
              <Controller
                name={name}
                control={control}
                render={({ field: { onChange, value } }) =>
                  options.length === 2 ? (
                    <FormControlLabel
                      sx={{ width: "100%" }}
                      control={
                        <Switch
                          checked={value === options[1].value}
                          onChange={(event) =>
                            onChange(
                              event.target.checked
                                ? options[1].value
                                : options[0].value,
                            )
                          }
                        />
                      }
                      label={translate(`${name}.description`)}
                    />
                  ) : (
                    <RadioGroup
                      value={value}
                      onChange={(event) => onChange(event.target.value)}
                      sx={{ width: "100%" }}
                    >
                      {options.map(({ value, label }) => (
                        <FormControlLabel
                          key={value}
                          value={value}
                          control={<Radio />}
                          label={label}
                        />
                      ))}
                    </RadioGroup>
                  )
                }
              />
            </Box>
            <Divider sx={{ margin: "10px 0px" }} />
          </React.Fragment>
        ))}

        <ActionButtons
          isLoading={isSaveLoading}
          isDirty={isDirty}
          reset={reset}
        />
      </form>
    </Container>
  );
};
